import React, { useMemo, useState } from "react";
import Select from "react-select";
import Swal from "sweetalert2";
import { addStoreEntriesMultiApi } from "../../../../services/inventory";
import { useGetStoreBalancesQuery } from "../../../../store/appApi";

const todayKey = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

let uid = 0;
const newRow = (base = {}) => ({
  id: ++uid,
  skuId: base.skuId || "",
  date: base.date || todayKey(),
  partyId: base.partyId || "", // supplier (add) or contractor/supplier (issue)
  partyType: base.partyType || "",
  party2Id: base.party2Id || "", // "Add & Issue": who it is issued to
  party2Type: base.party2Type || "",
  qty: "",
  price: "",
  invoiceNo: "",
  truckNo: "",
});

const selStyles = (invalid) => ({
  menuPortal: (b) => ({ ...b, zIndex: 2000 }),
  control: (b) => ({
    ...b,
    minHeight: 36,
    fontSize: 13,
    borderColor: invalid ? "#dc3545" : b.borderColor,
  }),
});

/**
 * Many stock entries at once. Entry type (add / issue) comes from the page.
 *  - "Same item for all rows" → single item, many entries (date / supplier / contractor wise)
 *  - off → many items, many entries
 */
const MultiEntryForm = ({
  entryType,
  skuOptions,
  issuedToOptions,
  skuUnits = {},
  onSaved,
}) => {
  const { data: balData } = useGetStoreBalancesQuery();
  const balances = balData?.balances || {};
  const [sameItem, setSameItem] = useState(true);
  const [sharedSku, setSharedSku] = useState("");
  const [rows, setRows] = useState(() => [newRow(), newRow(), newRow()]);
  const [rowErrors, setRowErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const isBoth = entryType === "both";
  const hasPrice = entryType === "add" || isBoth;
  const issueOptions = useMemo(
    () =>
      issuedToOptions.map((o) => ({
        value: o._id,
        type: o.type,
        label: `${o.name} — ${o.role || o.type}`,
      })),
    [issuedToOptions],
  );
  const partyOptions = useMemo(
    () =>
      entryType === "add" || isBoth
        ? issuedToOptions
            .filter((o) => o.type === "supplier")
            .map((o) => ({ value: o._id, type: "supplier", label: o.name }))
        : issuedToOptions.map((o) => ({
            value: o._id,
            type: o.type,
            label: `${o.name} — ${o.role || o.type}`,
          })),
    [issuedToOptions, entryType, isBoth],
  );

  // changing entry type invalidates the chosen supplier / recipient
  React.useEffect(() => {
    setRows((rs) => rs.map((r) => ({ ...r, partyId: "", partyType: "", party2Id: "", party2Type: "", price: "" })));
    setRowErrors({});
  }, [entryType]);

  const patch = (id, p) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...p } : r)));
    setRowErrors((e) => ({ ...e, [id]: undefined }));
  };
  const addRow = () =>
    setRows((rs) => {
      const last = rs[rs.length - 1] || {};
      // carry date / party / item forward so repeated entries are quick
      return [...rs, newRow({ skuId: last.skuId, date: last.date, partyId: last.partyId, partyType: last.partyType, party2Id: last.party2Id, party2Type: last.party2Type })];
    });
  const removeRow = (id) => setRows((rs) => (rs.length > 1 ? rs.filter((r) => r.id !== id) : rs));

  const filled = rows.filter((r) => r.qty !== "" || r.partyId || (!sameItem && r.skuId));
  const total = filled.reduce((s, r) => s + (Number(r.qty) || 0), 0);

  const submit = async () => {
    const used = rows.filter((r) => r.qty !== "" || r.partyId || r.party2Id || r.price !== "" || r.invoiceNo || r.truckNo || (!sameItem && r.skuId));
    if (!used.length) {
      Swal.fire({ icon: "warning", title: "No entries", text: "Fill at least one row." });
      return;
    }
    if (sameItem && !sharedSku) {
      Swal.fire({ icon: "warning", title: "Select item", text: "Choose the item for these entries." });
      return;
    }
    const payload = used.map((r) => ({
      row: rows.indexOf(r) + 1,
      skuId: sameItem ? sharedSku : r.skuId,
      entryType,
      date: r.date,
      qty: r.qty,
      invoiceNo: r.invoiceNo || undefined,
      truckNo: r.truckNo || undefined,
      ...(entryType === "add"
        ? { supplierId: r.partyId, price: r.price }
        : isBoth
          ? { supplierId: r.partyId, price: r.price, recipientId: r.party2Id, recipientType: r.party2Type }
          : { recipientId: r.partyId, recipientType: r.partyType }),
    }));
    try {
      setSaving(true);
      const res = await addStoreEntriesMultiApi(payload);
      Swal.fire({
        icon: "success",
        title: "Entries saved",
        text: `${res?.data?.created ?? payload.length} ${entryType === "add" ? "added to stock" : isBoth ? "received and issued" : "issued"}.`,
        timer: 2200,
        showConfirmButton: false,
      });
      setRows([newRow(), newRow(), newRow()]);
      setRowErrors({});
      onSaved && onSaved();
    } catch (err) {
      const list = err?.response?.data?.errors;
      if (Array.isArray(list) && list.length) {
        const byRowId = {};
        list.forEach((e) => {
          const r = rows[e.row - 1];
          if (r) byRowId[r.id] = [...(byRowId[r.id] || []), e.message];
        });
        setRowErrors(byRowId);
      }
      Swal.fire({
        icon: "error",
        title: "Not saved",
        text: err?.response?.data?.message || "Failed to save entries.",
      });
    } finally {
      setSaving(false);
    }
  };

  const partyLabel = entryType === "add" || isBoth ? "Supplier" : "Issued To";
  // columns before the stock-in-store / error lines span: # + item? + date + party(+party2) + qty + price? + bill + truck
  const span = 6 + (sameItem ? 0 : 1) + (hasPrice ? 1 : 0) + (isBoth ? 1 : 0);

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="sameItem"
            checked={sameItem}
            onChange={(e) => setSameItem(e.target.checked)}
          />
          <label className="form-check-label fw-semibold" htmlFor="sameItem">
            Same item for all rows
          </label>
        </div>
        {sameItem && (
          <div style={{ minWidth: 280, flex: "1 1 280px" }}>
            <Select
              placeholder="Search or select item…"
              isClearable
              options={skuOptions}
              value={skuOptions.find((o) => o.value === sharedSku) || null}
              onChange={(o) => setSharedSku(o ? o.value : "")}
              menuPortalTarget={document.body}
              styles={selStyles(false)}
            />
          </div>
        )}
      </div>

      <div className="table-responsive">
        <table className="table table-sm align-middle">
          <thead>
            <tr>
              <th style={{ width: 32 }}>#</th>
              {!sameItem && <th style={{ minWidth: 220 }}>Item *</th>}
              <th style={{ width: 150 }}>Date</th>
              <th style={{ minWidth: 220 }}>{partyLabel} *</th>
              {isBoth && <th style={{ minWidth: 220 }}>Issued To *</th>}
              <th style={{ width: 110 }}>Qty *</th>
              {hasPrice && <th style={{ width: 130 }}>Total Price ₹</th>}
              <th style={{ width: 150 }}>
                {entryType === "add" ? "Bill No" : isBoth ? "Bill / Voucher No" : "Voucher No"}
              </th>
              <th style={{ width: 130 }}>Truck No</th>
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const errs = rowErrors[r.id];
              return (
                <React.Fragment key={r.id}>
                  <tr className={errs ? "table-danger" : ""}>
                    <td>{i + 1}</td>
                    {!sameItem && (
                      <td>
                        <Select
                          placeholder="Item…"
                          options={skuOptions}
                          value={skuOptions.find((o) => o.value === r.skuId) || null}
                          onChange={(o) => patch(r.id, { skuId: o ? o.value : "" })}
                          menuPortalTarget={document.body}
                          styles={selStyles(!!errs)}
                        />
                      </td>
                    )}
                    <td>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={r.date}
                        max={todayKey()}
                        onChange={(e) => patch(r.id, { date: e.target.value })}
                      />
                    </td>
                    <td>
                      <Select
                        placeholder={`${partyLabel}…`}
                        options={partyOptions}
                        value={partyOptions.find((o) => o.value === r.partyId) || null}
                        onChange={(o) =>
                          patch(r.id, { partyId: o ? o.value : "", partyType: o ? o.type : "" })
                        }
                        menuPortalTarget={document.body}
                        styles={selStyles(!!errs)}
                      />
                    </td>
                    {isBoth && (
                      <td>
                        <Select
                          placeholder="Issued To…"
                          options={issueOptions}
                          value={issueOptions.find((o) => o.value === r.party2Id) || null}
                          onChange={(o) =>
                            patch(r.id, { party2Id: o ? o.value : "", party2Type: o ? o.type : "" })
                          }
                          menuPortalTarget={document.body}
                          styles={selStyles(!!errs)}
                        />
                      </td>
                    )}
                    <td>
                      <input
                        type="number"
                        min="1"
                        className="form-control form-control-sm"
                        value={r.qty}
                        onChange={(e) => patch(r.id, { qty: e.target.value })}
                      />
                    </td>
                    {hasPrice && (
                      <td>
                        <input
                          type="number"
                          min="0"
                          className="form-control form-control-sm"
                          value={r.price}
                          onChange={(e) => patch(r.id, { price: e.target.value })}
                        />
                      </td>
                    )}
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={r.invoiceNo}
                        onChange={(e) => patch(r.id, { invoiceNo: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm text-uppercase"
                        value={r.truckNo}
                        onChange={(e) => patch(r.id, { truckNo: e.target.value })}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-link text-danger p-0"
                        title="Remove row"
                        onClick={() => removeRow(r.id)}
                      >
                        <i className="fa fa-trash" />
                      </button>
                    </td>
                  </tr>
                  {(() => {
                    const sid = sameItem ? sharedSku : r.skuId;
                    if (!sid) return null;
                    const stock = Number(balances[sid] ?? 0);
                    const unit = skuUnits[sid] || "";
                    // total of this item across all rows in the grid
                    const planned = rows.reduce(
                      (n, x) =>
                        (sameItem ? sharedSku : x.skuId) === sid
                          ? n + (Number(x.qty) || 0)
                          : n,
                      0,
                    );
                    const short =
                      entryType === "issued" && planned > stock ? planned - stock : 0;
                    return (
                      <tr>
                        <td />
                        <td
                          colSpan={span}
                          className="small fw-semibold text-danger pt-0 border-0"
                        >
                          Stock in store: {stock.toLocaleString("en-IN")} {unit}
                          {short > 0 && ` — rows total ${planned}, short by ${short}`}
                        </td>
                      </tr>
                    );
                  })()}
                  {errs && (
                    <tr className="table-danger">
                      <td />
                      <td colSpan={span} className="small text-danger pt-0">
                        {errs.join(" ")}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={addRow}>
          <i className="fa fa-plus me-1" /> Add row
        </button>
        <div className="d-flex align-items-center gap-3">
          <span className="text-muted small">
            {filled.length} entr{filled.length === 1 ? "y" : "ies"} · total qty {total}
          </span>
          <button type="button" className="btn btn-primary" disabled={saving} onClick={submit}>
            {saving ? "Saving…" : entryType === "add" ? "Save all — Add to Stock" : isBoth ? "Save all — Add & Issue" : "Save all — Issue Stock"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MultiEntryForm;
